import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';

import { useGetTicketByIdQuery, useSendMessageMutation } from '../store/api/ticketApi';
import { useSseConnection } from '../hooks/useSseConnection';
import { API_BASE_URL } from '../utils/constants';
import { Loader } from '../components/ui/Loader';
import { Toaster } from '../components/ui/Toaster';
import type { Ticket, TicketMessage, TicketAttachment } from '../store/api/ticketApi';

function getFileType(mimeType: string) {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  return 'other';
}

function getStatusColor(status: string) {
  switch (status) {
    case 'open':
      return 'text-green-500 bg-green-500/10';
    case 'pending':
      return 'text-yellow-500 bg-yellow-500/10';
    case 'closed':
      return 'text-gray-500 bg-gray-500/10';
    default:
      return 'text-gray-500 bg-gray-500/10';
  }
}

function getPriorityColor(priority: string) {
  switch (priority) {
    case 'high':
      return 'text-red-500 bg-red-500/10';
    case 'medium':
      return 'text-yellow-500 bg-yellow-500/10';
    case 'low':
      return 'text-blue-500 bg-blue-500/10';
    default:
      return 'text-gray-500 bg-gray-500/10';
  }
}

export function TicketDetailsPage() {
  const { t, i18n } = useTranslation();
  const { ticketId } = useParams<{ ticketId: string }>();
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<FileList | null>(null);
  const [localMessages, setLocalMessages] = useState<TicketMessage[]>([]);
  const [localAttachments, setLocalAttachments] = useState<TicketAttachment[]>([]);
  const [localTicket, setLocalTicket] = useState<Ticket | null>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const isRTL = i18n.dir() === 'rtl';

  const { data, isLoading, error, refetch } = useGetTicketByIdQuery(ticketId!);
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();

  const ticket = localTicket || data?.ticket;
  const messages = localMessages.length > 0 ? localMessages : (data?.messages || []);
  const allAttachments = localAttachments.length > 0 ? localAttachments : (data?.attachments || []);

  // Update local state when API data changes
  useEffect(() => {
    if (data?.messages) {
      setLocalMessages(data.messages);
    }
    if (data?.attachments) {
      setLocalAttachments(data.attachments);
    }
    if (data?.ticket) {
      setLocalTicket(data.ticket);
    }
  }, [data]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleTicketNotification = useCallback((notification: any) => {
    if (notification.ticketId !== ticketId) return;

    if (notification.type === 'new_message' && notification.message) {
      setLocalMessages((prev) => {
        // Check if message already exists
        if (prev.some(m => m._id === notification.message._id)) {
          return prev;
        }
        return [...prev, notification.message];
      });
      if (notification.attachments) {
        setLocalAttachments((prev) => {
          // Add new attachments
          const newAttachments = notification.attachments.filter(
            (a: TicketAttachment) => !prev.some(pa => pa._id === a._id)
          );
          return [...prev, ...newAttachments];
        });
      }
    } else if (notification.type === 'status_updated' && notification.status) {
      setLocalTicket((prev) => {
        if (!prev) return prev;
        return { ...prev, status: notification.status };
      });
    } else if (notification.type === 'message_deleted' && notification.messageId) {
      setLocalMessages((prev) => prev.filter(m => m._id !== notification.messageId));
      setLocalAttachments((prev) => prev.filter(a => a.message !== notification.messageId));
    }
  }, [ticketId]);

  // Connect to ticket SSE
  const { connect } = useSseConnection({
    autoConnect: false,
    onTicketNotification: handleTicketNotification,
    sseUrl: `${API_BASE_URL}/tickets/sse`,
  });

  useEffect(() => {
    connect(`${API_BASE_URL}/tickets/sse`);
  }, [connect]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() && (!attachments || attachments.length === 0)) return;

    try {
      await sendMessage({
        ticketId: ticketId!,
        content: message,
        attachments: attachments || undefined,
      }).unwrap();
      setMessage('');
      setAttachments(null);
      refetch();
    } catch (err) {
      console.error('Failed to send message:', err);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMessageAttachments = (msgId: string) => {
    return allAttachments.filter(att => att.message === msgId);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="min-h-screen relative">
        <div className="container mx-auto px-4 py-16 content-overlay">
          <div className="max-w-6xl mx-auto">
            <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
              <p className="text-red-400 text-lg mb-4">{t('tickets.details.notFound')}</p>
              <Link
                to="/tickets"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-xl hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
              >
                {t('tickets.details.backToList')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <div className="container mx-auto px-4 py-16 content-overlay">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8 animate-slide-up">
            <Link
              to="/tickets"
              className="inline-flex items-center gap-2 text-neutral-medium hover:text-brand-accent transition mb-4"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {t('tickets.details.backToList')}
            </Link>

            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold text-brand-accent mb-4">
                  {ticket.title}
                </h1>
                <div className="flex flex-wrap items-center gap-3">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(ticket.status)}`}>
                    {t(`tickets.status.${ticket.status}`)}
                  </span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${getPriorityColor(ticket.priority)}`}>
                    {t(`tickets.priority.${ticket.priority}`)}
                  </span>
                  <span className="text-neutral-medium text-sm">
                    {t('tickets.details.category')}: {t(`tickets.category.${ticket.category}`)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="card-glass rounded-2xl shadow-2xl mb-6 overflow-hidden animate-scale-in">
            <div className="p-4 md:p-6 max-h-[60vh] overflow-y-auto space-y-4">
              {messages.map((msg: TicketMessage) => (
                  <div
                    key={msg._id}
                    className={`flex ${msg.senderType === 'user' ? (isRTL ? 'justify-start' : 'justify-end') : (isRTL ? 'justify-end' : 'justify-start')}`}
                  >
                    <div className={`max-w-[80%] ${msg.senderType === 'user' ? (isRTL ? 'items-start' : 'items-end') : (isRTL ? 'items-end' : 'items-start')} flex flex-col`}>
                      <div className={`ticket-chat-bubble px-4 py-3 rounded-2xl ${
                        msg.senderType === 'user'
                          ? 'ticket-chat-bubble--user ' + (isRTL ? 'rounded-bl-md' : 'rounded-br-md')
                          : 'ticket-chat-bubble--support ' + (isRTL ? 'rounded-br-md' : 'rounded-bl-md')
                      }`}>
                      <p className="font-medium mb-1">
                        {msg.senderType === 'user'
                          ? msg.sender?.firstName || t('tickets.details.you')
                          : t('tickets.details.support')}
                      </p>
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>
                    
                    {/* Attachments */}
                    {getMessageAttachments(msg._id).length > 0 && (
                      <div className="mt-2 space-y-2">
                        {getMessageAttachments(msg._id).map((att: TicketAttachment) => {
                          const fileType = getFileType(att.mimeType);
                          const src = `/api/v1/tickets/attachments/${att._id}/download`;
                          if (fileType === 'image') {
                            return (
                              <button
                                key={att._id}
                                type="button"
                                onClick={() => setLightboxSrc(src)}
                                className="block overflow-hidden rounded-xl max-w-xs"
                              >
                                <img
                                  src={src}
                                  alt={att.originalName}
                                  className="w-full h-auto object-cover rounded-xl hover:scale-105 transition-transform duration-200 cursor-pointer"
                                />
                              </button>
                            );
                          }
                          if (fileType === 'video') {
                            return (
                              <div key={att._id} className="max-w-xs">
                                <video
                                  src={src}
                                  controls
                                  preload="metadata"
                                  className="w-full rounded-xl cursor-pointer"
                                  onClick={() => setLightboxSrc(src)}
                                />
                              </div>
                            );
                          }
                          return (
                            <a
                              key={att._id}
                              href={src}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm ${
                                msg.senderType === 'user'
                                  ? 'bg-brand-accent/20 text-brand-accent'
                                  : 'bg-neutral/10 text-neutral-light'
                              }`}
                            >
                              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                              </svg>
                              <span className="truncate">{att.originalName}</span>
                            </a>
                          );
                        })}
                      </div>
                    )}

                    <p className="text-xs text-neutral-medium mt-1">
                      {formatDate(msg.createdAt)}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Send Message Form */}
          {ticket.status !== 'closed' && (
            <form onSubmit={handleSend} className="space-y-4 animate-scale-in">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder={t('tickets.details.messagePlaceholder')}
                rows={3}
                className="w-full px-4 py-3 rounded-xl border border-neutral/20 bg-neutral/5 text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent/50 resize-none"
              />
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    onChange={(e) => setAttachments(e.target.files)}
                    className="sr-only"
                  />
                  <label
                    htmlFor="file-upload"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-neutral/20 bg-neutral/5 text-brand-accent hover:bg-brand-accent/10 transition cursor-pointer text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                    {i18n.language.startsWith('ar') ? 'ارفع ملف' : 'Choose Files'}
                  </label>
                </div>
                <button
                  type="submit"
                  disabled={isSending || (!message.trim() && (!attachments || attachments.length === 0))}
                  className="px-6 py-3 bg-brand-accent text-brand-primary font-semibold rounded-xl hover:shadow-lg hover:shadow-brand-accent/50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isSending ? (
                    <>
                      <Loader size="sm" />
                      {t('tickets.details.sending')}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      {t('tickets.details.send')}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {ticket.status === 'closed' && (
            <div className="p-6 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl text-center animate-scale-in">
              <p className="text-yellow-400">{t('tickets.details.closed')}</p>
            </div>
          )}

          {/* Back Button */}
          <div className="text-center mt-6">
            <Link
              to="/dashboard"
              className="inline-block px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300"
            >
              {t('common.back')}
            </Link>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxSrc(null)}
        >
          <button
            type="button"
            className="absolute top-4 right-4 text-white text-3xl hover:text-neutral-300 transition z-10"
            onClick={() => setLightboxSrc(null)}
          >
            ✕
          </button>
          <div className="max-w-full max-h-full" onClick={(e) => e.stopPropagation()}>
            {lightboxSrc.match(/\.(mp4|webm|ogg)/i) ? (
              <video src={lightboxSrc} controls autoPlay className="max-w-full max-h-[90vh] rounded-xl" />
            ) : (
              <img src={lightboxSrc} alt="" className="max-w-full max-h-[90vh] object-contain rounded-xl" />
            )}
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
}
